import { atom } from "jotai";
import { atomWithReducer } from "jotai/utils";

import { EventChargePayerRowData } from "@whoosh/design-system/src/components/display/ChargePayerRow";

import { currentProfileAtom } from "../../../features/Club/atoms";
import {
  GuestRegistrationInput,
  MemberRegistrationInput,
} from "../hooks/__generated__/useAddEventSignUpToConfirmedMutation.graphql";
import { NewGuestRegistrationInput } from "../hooks/__generated__/useUpdateEventSignUpMutation.graphql";
import { EventSignUpRegistration, EventSignUpRow } from "../types";

export type EventGroupData = {
  minMemberCount: number;
  maxMemberCount: number;
  maxGuestCount: number;
  minGuestCount: number;
  allowedGroupSizes: (number | null)[];
};

export const InitialGroupData: EventGroupData = {
  minMemberCount: 1,
  maxMemberCount: 1,
  maxGuestCount: 0,
  minGuestCount: 0,
  allowedGroupSizes: [null],
};

export const groupEventAtom = atom<EventGroupData>(InitialGroupData);

export const isGroupEventAtom = atom<boolean>((get) => {
  const data = get(groupEventAtom);

  return (
    data?.maxGuestCount !== 0 ||
    data?.maxMemberCount !== 1 ||
    data?.minMemberCount !== 1 ||
    data?.minGuestCount !== 0
  );
});

export const groupSizeAtom = atom<number>((get) => {
  const data = get(groupEventAtom);

  const validGroupSizes: number[] =
    data?.allowedGroupSizes.map((s) => s || 0) || [];

  const maxGroupSize = Math.max(...validGroupSizes);

  return maxGroupSize || 0;
});

const defaultEventSignupState: EventSignUpRow & { isDirty: boolean } = {
  id: null,
  tempID: null,
  checkedIn: "NONE",
  waitlistIndex: null,
  registrations: [],
  isDirty: false,
};

type eventSignupReducerActions =
  | { type: "clear" }
  | { type: "initialize"; payload: EventSignUpRow }
  | { type: "addPlayer"; payload: EventSignUpRegistration }
  | { type: "update"; payload: EventSignUpRow }
  | { type: "removePlayer"; payload: EventSignUpRegistration }
  /**
   * Todo: Rewrite this state management so it's not using a reducer and we can actually access
   * other data.
   */
  | {
      type: "quickUpdatePrimaryChargePaymentOption";
      paymentOption: "you_pay_for_everyone" | "everyone_pays_for_themselves";
      currentUserMemberId: string;
    }
  | {
      type: "updatePrimaryChargePaymentOption";
      payerId: string;
      payedForIds: string[];
    };
//Add an action to handle the 3 possible responses for primary charge who pays data...
//Add an action to update just a single person's who pays data.

const eventSignUpsReducer = (
  state: EventSignUpRow,
  action: eventSignupReducerActions
) => {
  switch (action.type) {
    case "clear":
      return defaultEventSignupState;

    case "initialize":
      return { isDirty: false, ...state, ...action.payload };

    case "addPlayer":
      return {
        ...state,
        registrations: [...state.registrations, action.payload],
        isDirty: true,
      };
    case "removePlayer":
      const matchingID = action.payload.registrant.playerId;
      const newRegistrations = state.registrations.filter(
        (r) => r.registrant.playerId !== matchingID
      );

      return { ...state, registrations: newRegistrations, isDirty: true };
    case "update":
      return { ...state, ...action.payload, isDirty: true };
    case "updatePrimaryChargePaymentOption":
      const updatedRegistrations = state.registrations.map((r) => {
        //IF we passed up the player ID as someone we are paying for
        //update the payingMemberId
        if (
          r.registrant.playerId &&
          action.payedForIds.includes(r.registrant.playerId)
        ) {
          return {
            ...r,
            payingMemberId: action.payerId,
          };
        } else if (
          r.type === "MEMBER" &&
          r.registrant.playerId &&
          r.payingMemberId === action.payerId
        ) {
          /**
           * If we are a member, that WAS being payed for by the passed ID,
           * and we are no longer being payed for - then we are paying for ourself.
           */
          return {
            ...r,
            payingMemberId: r.registrant.playerId,
          };
        } else if (r.type !== "MEMBER" && r.payingMemberId === action.payerId) {
          /**
           * If we are not a member, and no longer being payed for... then I don't know how to handle it
           * TODO: UPDATE this case
           */
          return {
            ...r,
          };
        } else {
          return r;
        }
      });
      return { ...state, registrations: updatedRegistrations };
    case "quickUpdatePrimaryChargePaymentOption":
      //If you pay for everyone, we just update all the paying member Ids to be you.
      if (action.paymentOption === "you_pay_for_everyone") {
        return {
          ...state,
          registrations: state.registrations.map((r) => {
            return {
              ...r,
              payingMemberId: action.currentUserMemberId,
            };
          }),
        };
      }
      //IF everyone pays for themselves, we update all MEMBERS to pay for themselves.
      //We don't touch guests as they cannot pay for themselves.
      else if (action.paymentOption === "everyone_pays_for_themselves") {
        return {
          ...state,
          registrations: state.registrations.map((r) => {
            const isMember = r.type === "MEMBER";
            const curRegistrantMemberId = isMember
              ? r.registrant.playerId
              : null;
            return isMember && curRegistrantMemberId
              ? {
                  ...r,
                  payingMemberId: curRegistrantMemberId,
                }
              : r;
          }),
        };
      }
      return state;
    default:
      return state;
  }
};

export const eventSignUpsReducerAtom = atomWithReducer(
  defaultEventSignupState,
  eventSignUpsReducer
);

export const eventSignupsMemberRegistrationsAtom = atom<
  MemberRegistrationInput[]
>((get) => {
  const data = get(eventSignUpsReducerAtom);

  return data.registrations
    .filter((r) => r.type === "MEMBER")
    .map((m, i) => ({
      playerId: m.registrant.playerId || "",
      index: m.index || i,
      formResponses: m.formResponses,
      payingMemberId: m.payingMemberId,
    }));
});

export const eventSignupsGuestRegistrationsAtom = atom<
  GuestRegistrationInput[]
>((get) => {
  const data = get(eventSignUpsReducerAtom);

  return data.registrations
    .filter((r) => r.type === "GUEST")
    .map((m, i) => ({
      playerId: m.registrant.playerId || "",
      index: m.index || i,
      formResponses: m.formResponses,
      payingMemberId: m.payingMemberId,
    }));
});

export const eventSignupsNewGuestRegistrationsAtom = atom<
  NewGuestRegistrationInput[]
>((get) => {
  const data = get(eventSignUpsReducerAtom);

  return data.registrations
    .filter((r) => r.type === "New Guest")
    .map((g, i) => ({
      index: g.index || i,
      email: g.registrant.email,
      name: `${g.registrant.firstName} ${g.registrant.lastName}`,
      payingMemberId: g.payingMemberId,
    }));
});

/**
 * TODO: Event Charge Data - should be moved out into features probably
 */

//TODO: Obviously a turbo minimal representation. This will need to be expanded as we implement
//additional primary charge rules.
export const primaryChargeRulePriceAtom = atom<number | null>(null);

export const eventPrimaryChargePaymentDataAtom = atom<
  EventChargePayerRowData[]
>((get) => {
  const currentProfile = get(currentProfileAtom);
  const currentUserMemberId = currentProfile?.memberId;
  const data = get(eventSignUpsReducerAtom);
  const price = get(primaryChargeRulePriceAtom) ?? 0;
  //Get all the rows that are paying for themselves.
  const onlySelfPayers: EventChargePayerRowData[] = data.registrations
    .filter((cur) => {
      const isMember = cur.type === "MEMBER";
      const isPayingForSelf =
        isMember && cur.payingMemberId === cur.registrant.playerId;
      return isPayingForSelf;
    })
    .flatMap((cur) => {
      return cur.registrant.playerId
        ? [
            {
              payer: {
                firstName: cur.registrant.firstName ?? null,
                lastName: cur.registrant.lastName ?? null,
                id: cur.registrant.playerId,
                price,
                profileImageId: cur.registrant.profileImage?.publicId,
                detail: `MEMBER NUMBER EVENTUALLY GOES HERE`,
                type: "MEMBER",
                isCurrentUser: cur.registrant.playerId === currentUserMemberId,
              },
              additionalChargeRows: [],
            },
          ]
        : [];
    });
  //Add anyone else that is being payed for beneath them.
  //Default anyone without a payer ID to be paid for by the first row.
  const chargeData: EventChargePayerRowData[] = onlySelfPayers.map((cur) => {
    const payerId = cur.payer.id;
    const additionalChargeRows = data.registrations
      .filter((cur) => {
        const isMember = cur.type === "MEMBER";
        const isPayingForSelf =
          isMember && cur.payingMemberId === cur.registrant.playerId;
        return (
          !isPayingForSelf &&
          (cur.payingMemberId === payerId || !cur.payingMemberId)
        );
      })
      .flatMap((cur) => {
        return cur.registrant.playerId
          ? [
              {
                firstName: cur.registrant.firstName ?? null,
                lastName: cur.registrant.lastName ?? null,
                id: cur.registrant.playerId,
                price,
                profileImageId: cur.registrant.profileImage?.publicId,
                detail: `MEMBER NUMBER EVENTUALLY GOES HERE`,
                type: cur.type ? cur.type : "MEMBER",
              },
            ]
          : [];
      });
    return {
      payer: cur.payer,
      additionalChargeRows,
    };
  });

  return chargeData;
});

/** TODO: Clean up
 *
 * a bit awkward. Because the original group atom is a reducer I cannot
 * access outside info - which makes it awkward to work with. I added
 * this sort of passthrough action atom to allow the view components to call
 * without having to leak all the other data into those components
 */
type EventPrimaryChargeUpdateActions = {
  type: "quickUpdatePrimaryChargePaymentOption";
  paymentOption: "you_pay_for_everyone" | "everyone_pays_for_themselves";
};

export const eventPrimaryChargeUpdateAtom = atom(
  null,
  (get, set, action: EventPrimaryChargeUpdateActions) => {
    const currentProfile = get(currentProfileAtom);
    const currentUserMemberId = currentProfile?.memberId;

    switch (action.type) {
      case "quickUpdatePrimaryChargePaymentOption":
        if (currentUserMemberId) {
          set(eventSignUpsReducerAtom, {
            type: "quickUpdatePrimaryChargePaymentOption",
            paymentOption: action.paymentOption,
            currentUserMemberId,
          });
        }

        break;
      default:
        break;
    }
  }
);
